import { Avatar, Button, Icons, OpennessMeter } from "@jod/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type AroundTable, customerBackendApi } from "../features/api/backend.ts";
import { t, useT } from "../features/i18n/index.ts";
import { useSession } from "../features/session/session-context.tsx";
import { getMode } from "../features/tonight/mode.ts";
import { mockTables } from "../mocks/data.ts";

export const Route = createFileRoute("/tables/$tableId")({
  component: TableDetailScreen,
});

/** Unified detail shape so backend (Around) and mock tables share one render. */
interface DetailTable {
  id: string;
  code: string;
  zone: string;
  statusTh: string;
  mode: "private" | "soft_open" | "lets_cheers" | "with_friends";
  emoji: string;
  members: string[];
  bioTh: string;
  partyId?: string;
  acceptingGifts: boolean;
  acceptingContactRequests: boolean;
}

function detailFromAround(entry: AroundTable): DetailTable {
  return {
    id: entry.tableId,
    code: entry.code,
    zone: entry.label,
    statusTh: entry.acceptingGifts ? t("around.acceptingGifts") : t("around.openTonight"),
    mode: "soft_open",
    emoji: "🍸",
    members: entry.partyName
      ? [entry.partyName]
      : [t("table.peopleCount", { count: entry.partySize })],
    bioTh: t("table.bioLine", { label: entry.label, count: entry.partySize }),
    partyId: entry.partyId,
    acceptingGifts: entry.acceptingGifts,
    acceptingContactRequests: entry.acceptingContactRequests,
  };
}

function TableDetailScreen() {
  const translate = useT();
  const { tableId } = Route.useParams();
  const navigate = useNavigate();
  const { canStartSocial, sendContactRequest, blockTable, reportTable, toast } = useSession();

  const backendEnabled = customerBackendApi !== null;
  const aroundQuery = useQuery({
    queryKey: ["customer", "around"],
    queryFn: () =>
      customerBackendApi?.listAroundTables() ??
      Promise.reject(new Error("Customer backend API unavailable")),
    enabled: backendEnabled,
    refetchInterval: 10_000,
  });
  const table: DetailTable | undefined = backendEnabled
    ? aroundQuery.data?.map(detailFromAround).find((t) => t.id === tableId)
    : mockTables
        .map((t) => ({
          id: t.id,
          code: t.code,
          zone: t.zone,
          statusTh: t.statusTh,
          mode: t.mode,
          emoji: t.emoji,
          members: t.members,
          bioTh: t.bioTh,
          partyId: undefined,
          acceptingGifts: true,
          acceptingContactRequests: true,
        }))
        .find((t) => t.id === tableId);

  if (backendEnabled && aroundQuery.isLoading) {
    return (
      <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col items-center justify-center gap-4 px-8 text-center">
        <p className="text-cream-3 text-sm">{translate("table.loading")}</p>
      </div>
    );
  }

  if (!table) {
    return (
      <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col items-center justify-center gap-4 px-8 text-center">
        <h1 className="h-display text-[1.5rem]">{translate("table.notFound")}</h1>
        <Button variant="ghost" onClick={() => navigate({ to: "/around" })}>
          {translate("table.backToAround")}
        </Button>
      </div>
    );
  }

  const tm = getMode(table.mode);
  const localGiftGuard = canStartSocial({ toTable: table.code, kind: "gift" });
  const localContactGuard = canStartSocial({ toTable: table.code, kind: "contact" });
  const giftGuard = !table.acceptingGifts
    ? { ok: false, reason: translate("table.giftsClosed") }
    : localGiftGuard;
  const contactGuard = !table.acceptingContactRequests
    ? { ok: false, reason: translate("table.contactClosed") }
    : localContactGuard;

  const requestContact = (subject: string, presetKey: string) => {
    const result = sendContactRequest({
      toTable: table.code,
      subject,
      message:
        subject === "IG" ? translate("table.askIgMessage") : translate("table.softGreetMessage"),
      targetPartyId: table.partyId,
      messagePresetKey: presetKey,
    });
    toast(
      result.ok
        ? translate("table.requestSentToast", { subject, table: table.code })
        : (result.reason ?? translate("common.cantSendYet")),
    );
  };

  return (
    <div className="mx-auto w-full max-w-md px-[18px] py-6">
      <button
        type="button"
        className="mb-4 flex items-center gap-1 text-cream-3 text-sm"
        onClick={() => navigate({ to: "/around" })}
      >
        <Icons.IconBack size={18} /> {translate("common.back")}
      </button>

      <div className="glass-hi relative overflow-hidden rounded-xl p-5">
        <div className="jod-sheen" aria-hidden="true" />
        <div className="relative flex items-center gap-4">
          <Avatar glyph={table.emoji} size={60} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h1 className="h-display text-[1.4rem]">
                {translate("common.table", { code: table.code })}
              </h1>
              <OpennessMeter level={tm.openness} tone={tm.tone} />
            </div>
            <div className="text-cream-3 text-sm">
              {table.zone} · {table.statusTh}
            </div>
          </div>
        </div>
        <p className="relative mt-3 text-cream-2 text-sm leading-relaxed">{table.bioTh}</p>
        <div className="relative mt-3 flex flex-wrap gap-2">
          {table.members.map((m) => (
            <span
              key={m}
              className="rounded-full border border-hairline bg-[oklch(100%_0_0_/_4%)] px-3 py-1 text-cream-2 text-xs"
            >
              {m}
            </span>
          ))}
        </div>
      </div>

      {/* Respectful minimal card: this table's real data is limited to what
          the Around feed exposes (zone/status/mode/members, shown above).
          There is no per-table extended profile (photos/vibes/IG) API yet —
          showing fabricated content here would misrepresent another patron's
          real data. IG/contact details are only ever shared directly between
          patrons after a mutually accepted ContactRequest (see /requests). */}
      <div className="mt-4 glass flex items-center gap-2.5 rounded-xl p-4 text-cream-3 text-xs">
        <Icons.IconLock size={14} />
        {translate("table.consentShareNote")}
      </div>

      {!giftGuard.ok || !contactGuard.ok ? (
        <div className="glass mt-4 rounded-lg p-3 text-cream-3 text-xs">
          {!giftGuard.ok ? (
            <p>{translate("table.giftGuardPrefix", { reason: giftGuard.reason ?? "" })}</p>
          ) : null}
          {!contactGuard.ok ? (
            <p>{translate("table.contactGuardPrefix", { reason: contactGuard.reason ?? "" })}</p>
          ) : null}
        </div>
      ) : null}

      <div className="mt-4 space-y-2.5">
        <Button
          size="lg"
          variant="accent"
          className="w-full"
          disabled={!giftGuard.ok}
          onClick={() => navigate({ to: "/send-drink", search: { to: table.code } })}
        >
          <Icons.IconCheers size={20} /> {translate("home.sendDrinkTitle")}
        </Button>
        <Button
          variant="ghost"
          className="w-full"
          disabled={!contactGuard.ok}
          onClick={() => requestContact("IG", "ig")}
        >
          <Icons.IconIG size={16} /> {translate("table.askIg")}
        </Button>
        <Button
          variant="ghost"
          className="w-full"
          disabled={!contactGuard.ok}
          onClick={() => requestContact("chat", "chat")}
        >
          <Icons.IconChat size={16} /> {translate("table.softGreet")}
        </Button>
        <div className="flex gap-2.5 pt-1">
          <Button
            variant="destructive"
            className="flex-1"
            onClick={() => {
              blockTable(table.code, table.partyId);
              toast(translate("table.blockedToast"));
              navigate({ to: "/around" });
            }}
          >
            <Icons.IconShield size={16} /> {translate("common.block")}
          </Button>
          <Button
            variant="destructive"
            className="flex-1"
            onClick={() => {
              reportTable(table.code, "guest_report", table.partyId);
              toast(translate("table.reportToast"));
            }}
          >
            <Icons.IconFlag size={16} /> {translate("common.report")}
          </Button>
        </div>
      </div>
    </div>
  );
}
